import {DiffFileHeader} from '@github-ui/diff-file-header'
import type React from 'react'
import {forwardRef, memo, useCallback, useId, useMemo, useState} from 'react'
import {
  CommentSubjectType,
  type DiffEntry,
  type DiffUser,
  type FileDiffReference,
  type RichDiff as RichDiffType,
} from '../types'
import type {Thread} from '../conversation-types'
import {StartThreadComment} from './conversations/StartThreadComment'
import {RichDiff, type RichDiffProps} from './RichDiff'
import {DiffLines} from './DiffLines'
import styles from './Diff.module.css'
import {SubmoduleDiff} from './SubmoduleDiff'
import {useCopilotChatReference} from '../hooks/use-copilot-chat-reference'
import type {Repository} from '@github-ui/current-repository'
import {Blankslate} from '@primer/react/experimental'
import {AlertIcon, InfoIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {FileMarkers} from './conversations/FileMarkers'
import type {InjectedContextlinesSliceOpts} from '../stores/slices/injected-context-lines'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {DiffStoreProvider} from '../stores/DiffStore'
import {useCreateReviewCommentMutation} from '@github-ui/pull-requests/use-create-review-comment-mutation'
import {useSelectedRefContext} from '@github-ui/pull-requests/contexts/SelectedRefContext'
import {useComparisonInfo, useIsSingleFileMode} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ReviewCommentFactory} from '../helpers/review-comment-factory'
import {DiffFeature} from '@github-ui/diffs/types'
import {FileLevelCode} from '../stores/slices/move-new-comment'
import type {DiffLineKey} from '../stores/slices/helpers/diff-lines-slice-helpers'

const defaultHiddenFeatures = new Set<DiffFeature>()
const fileDiffLineKey = `b:0-l:null-r:null` as DiffLineKey

export interface RichDiffState {
  richDiff?: RichDiffType
  showRichDiff: boolean
  onToggleRichDiff: (showRichDiff: boolean) => void
  queryConfig?: RichDiffProps['queryConfig']
}

/**
 * DiffProps extends the Diff data type by adding additional,
 * more UI-related properties.
 */
export interface DiffProps extends DiffEntry {
  addInjectedContextLines: InjectedContextlinesSliceOpts['addInjectedContextLines']
  addFileCommentButtonRef?: React.RefObject<HTMLButtonElement | null>
  collapsed: boolean
  diffManuallyExpanded: boolean
  fileComments: Thread[]
  headerStickyOffset?: number
  hiddenFeatures?: Set<DiffFeature>
  isAddingFileComment?: boolean
  isSelected?: boolean
  rightSideContent: React.ReactNode | null
  setIsAddingFileComment: (value: boolean) => void
  pullRequestGlobalRelayId?: string
  leftSideContent: React.ReactNode | null
  onToggleCollapse: (event: React.MouseEvent, collapsed: boolean) => void
  currentUser: DiffUser
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  basePath: string
}

export interface DiffContextProps extends DiffProps {
  canExpandOrCollapseLines: boolean
  hasExpandedAllRanges: boolean
  expandAllContextLines: () => void
  loadDiff: () => Promise<void>
  richDiffState?: RichDiffState
}

const DiffUnmemoized = forwardRef(function DiffUnmemoized(
  {
    addFileCommentButtonRef,
    collapsed,
    currentUser,
    diffLines,
    diffManuallyExpanded,
    diffSize,
    helpUrl,
    fileComments,
    headerStickyOffset = 0,
    hiddenFeatures = defaultHiddenFeatures,
    isAddingFileComment,
    isBinary,
    isSelected = false,
    isSubmodule,
    isTooBig,
    leftSideContent,
    linesAdded,
    linesChanged,
    linesDeleted,
    newTreeEntry,
    newCommitOid,
    objectId,
    oldTreeEntry,
    oldCommitOid,
    onToggleCollapse,
    path,
    pathDigest,
    pullRequestGlobalRelayId,
    repository,
    richDiffState,
    rightSideContent,
    setIsAddingFileComment,
    hasExpandedAllRanges,
    expandAllContextLines,
    canExpandOrCollapseLines,
    status,
    submodule,
    truncatedReason,
    addInjectedContextLines,
    loadDiff,
  }: DiffContextProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const showSubmodule = isSubmodule && !!submodule
  const showDiffLines = !showSubmodule && !richDiffState?.showRichDiff
  const [linesManuallyUnhidden, setLinesManuallyUnhidden] = useState(diffManuallyExpanded)
  const isInSingleFileMode = useIsSingleFileMode()

  const copilotChatReference: FileDiffReference | undefined = useCopilotChatReference({
    isBinary,
    isSubmodule,
    path,
    status,
    repository,
    newCommitOid,
    newTreeEntry,
    oldCommitOid,
    oldTreeEntry,
    pathDigest,
    hasCopilotAccess: currentUser.hasCopilotAccess,
  })

  const onHandleLoadDiff = async () => {
    await loadDiff()

    setLinesManuallyUnhidden(true)
  }

  // TODO: remove this use of useSelectedRefContext when we remove :cotd_ui Feature Flag
  const {endOid, startOid} = useSelectedRefContext()
  const commentOutsideOfTheDiffEnabled = useFeatureFlag('cotd_ui')
  const {selectedRange: selectedDiffRange} = useComparisonInfo()
  const {mutate: createReviewCommentMutation} = useCreateReviewCommentMutation()
  const handleBeginAddFileComment = useCallback(() => {
    setIsAddingFileComment(true)
  }, [setIsAddingFileComment])

  const startNewConversationForLineOrFileMap = useMemo(() => {
    return {
      [FileLevelCode]: {startNewComment: handleBeginAddFileComment, diffLineKey: fileDiffLineKey},
    }
  }, [handleBeginAddFileComment])

  const handleAddFileComment = ({
    onCompleted,
    onError,
    submitBatch,
    text,
  }: {
    onCompleted?: (threadId: string, commentDatabaseId: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean | undefined
    text: string
  }) => {
    if (commentOutsideOfTheDiffEnabled) {
      const requestData = new ReviewCommentFactory({
        filePath: path,
        selectedDiffRange,
        submitBatch,
        text,
      }).fileRequestData()
      createReviewCommentMutation(requestData, {
        onError,
        onSuccess: data => {
          onCompleted?.(data.thread.id, data.comment.databaseId)
          setIsAddingFileComment(false)
        },
      })
    } else {
      createReviewCommentMutation(
        {
          text,
          path,
          submitBatch,
          subjectType: CommentSubjectType.File,
          comparisonEndOid: endOid,
          comparisonStartOid: startOid,
        },
        {
          onError,
          onSuccess: data => {
            onCompleted?.(data.thread.id, data.comment.databaseId)
            setIsAddingFileComment(false)
          },
        },
      )
    }
  }

  const onCancelComment = () => {
    setIsAddingFileComment(false)
    addFileCommentButtonRef?.current?.focus()
  }

  const viewerData = useMemo(() => {
    return {
      avatarUrl: currentUser?.avatarURL ?? '',
      diffViewPreference: currentUser?.splitPreference,
      login: currentUser?.login ?? '',
      lineSpacingPreference: currentUser?.lineSpacing,
      shouldDisplayComments: currentUser?.shouldDisplayComments,
      shouldDisplayEditOptions: currentUser?.shouldDisplayEditOptions,
      tabSizePreference: currentUser?.tabSize ?? 4,
      viewerCanComment: currentUser?.canComment,
      viewerCanApplySuggestion: currentUser?.canApplySuggestion,
      commentsPreference: currentUser?.commentsPreference,
    }
  }, [currentUser])

  const id = useId()
  const headingId = `heading-${id}`

  const isSplitView = useMemo(() => {
    const searchParams = new URLSearchParams(ssrSafeWindow?.location.search)
    const diffSearchParams = searchParams.get('diff')
    if (diffSearchParams === 'split') return true

    return currentUser.splitPreference === 'split'
  }, [currentUser.splitPreference])

  const fileLineCount = newTreeEntry?.lineCount ?? oldTreeEntry?.lineCount ?? 0

  return (
    <DiffStoreProvider
      addInjectedContextLines={addInjectedContextLines}
      diffAnchor={`diff-${pathDigest}`}
      diffLines={diffLines}
      fileLineCount={fileLineCount}
      path={path}
      startNewConversationForLineOrFileMap={startNewConversationForLineOrFileMap}
      hiddenFeatures={hiddenFeatures}
      isSplitView={isSplitView}
      oldTreeEntry={oldTreeEntry}
      newTreeEntry={newTreeEntry}
    >
      <div
        role="region"
        ref={ref}
        aria-labelledby={headingId}
        id={`diff-${pathDigest}`}
        className={clsx(styles.diffTargetable, styles.diff)}
        data-targeted={isSelected}
        key={`${pathDigest}_${diffLines.length}`}
      >
        <div
          className={styles.diffHeaderWrapper}
          style={{
            '--header-sticky-offset': `${headerStickyOffset}px`,
          }}
        >
          <DiffFileHeader
            headingId={headingId}
            hiddenFeatures={hiddenFeatures}
            areLinesExpanded={hasExpandedAllRanges}
            canExpandOrCollapseLines={canExpandOrCollapseLines}
            fileLinkHref={`#diff-${pathDigest}`}
            isCollapsed={collapsed}
            isBinary={isBinary}
            size={diffSize}
            canToggleRichDiff={richDiffState?.richDiff?.canToggleRichDiff}
            linesAdded={linesAdded}
            linesChanged={linesChanged}
            linesDeleted={linesDeleted}
            newMode={newTreeEntry?.mode}
            newPath={newTreeEntry?.path}
            oldMode={oldTreeEntry?.mode}
            oldPath={oldTreeEntry?.path}
            patchStatus={status}
            path={path}
            onToggleExpandAllLines={expandAllContextLines}
            onToggleFileCollapsed={event => onToggleCollapse(event, !collapsed)}
            onToggleDiffDisplay={richDiffState?.onToggleRichDiff}
            additionalLeftSideContent={leftSideContent}
            rightSideContent={rightSideContent}
            showRichDiff={richDiffState?.showRichDiff}
            isInSingleFileMode={isInSingleFileMode}
          />
        </div>
        {!collapsed ? (
          <div className="border position-relative rounded-bottom-2">
            <>
              {fileComments.length > 0 && (
                <FileMarkers
                  conversationListThreads={fileComments}
                  filePath={path}
                  subjectId={pullRequestGlobalRelayId || ''}
                />
              )}
              {isAddingFileComment && (
                <div
                  className={clsx(
                    'border rounded-2 color-border-default m-1 rounded-2 bgColor-default',
                    styles.diffAddFileThread,
                  )}
                >
                  <StartThreadComment
                    batchingEnabled
                    filePath={path}
                    fileLevelComment
                    isLeftSide={undefined}
                    subjectId={pullRequestGlobalRelayId || ''}
                    viewerData={viewerData}
                    onAddComment={handleAddFileComment}
                    onClose={onCancelComment}
                  />
                </div>
              )}
            </>
            {showSubmodule && <SubmoduleDiff submodule={submodule} />}

            {richDiffState?.showRichDiff && (
              <RichDiff queryConfig={richDiffState.queryConfig} initialData={richDiffState.richDiff} />
            )}

            {showDiffLines && (
              <DiffLines
                hasExpandedAllRanges={hasExpandedAllRanges}
                copilotChatReference={copilotChatReference}
                diffEntryData={{
                  diffLines,
                  isBinary,
                  isTooBig,
                  linesChanged,
                  newTreeEntry,
                  newCommitOid,
                  objectId,
                  oldTreeEntry,
                  oldCommitOid,
                  path,
                  pathDigest,
                  status,
                  truncatedReason,
                }}
                baseHelpUrl={helpUrl}
                subjectId={pullRequestGlobalRelayId || ''}
                viewerData={viewerData}
                isSplitView={isSplitView}
                diffLinesManuallyUnhidden={linesManuallyUnhidden}
                onHandleLoadDiff={onHandleLoadDiff}
              />
            )}
          </div>
        ) : null}
      </div>
      {isInSingleFileMode && (
        <div className={styles.diffFooter}>
          <InfoIcon size={16} aria-hidden="true" />
          <span className="text-center">
            Due to the large number of changes in this pull request, only one file is being shown at a time.
          </span>
        </div>
      )}
    </DiffStoreProvider>
  )
})

type DiffErrorFallbackProps = Pick<
  DiffEntry,
  'path' | 'pathDigest' | 'linesAdded' | 'linesChanged' | 'linesDeleted' | 'status' | 'newTreeEntry' | 'oldTreeEntry'
>

export function DiffErrorFallback({
  path,
  pathDigest,
  linesAdded,
  linesChanged,
  linesDeleted,
  oldTreeEntry,
  newTreeEntry,
  status,
}: DiffErrorFallbackProps) {
  const id = useId()
  const headingId = `heading-${id}`

  const [isCollapsed, setIsCollapsed] = useState(false)

  return (
    <div
      role="region"
      aria-labelledby={headingId}
      id={`diff-${pathDigest}`}
      className={clsx(styles.diffTargetable, styles.diff)}
      key={`${pathDigest}_error`}
    >
      <div className={styles.diffHeaderWrapper}>
        <DiffFileHeader
          headingId={headingId}
          isCollapsed={isCollapsed}
          isBinary={false}
          linesAdded={linesAdded}
          linesChanged={linesChanged}
          linesDeleted={linesDeleted}
          newMode={newTreeEntry?.mode}
          newPath={newTreeEntry?.path}
          oldMode={oldTreeEntry?.mode}
          oldPath={oldTreeEntry?.path}
          patchStatus={status}
          path={path}
          onToggleFileCollapsed={() => setIsCollapsed(!isCollapsed)}
        />
      </div>
      {!isCollapsed && (
        <div className="border position-relative rounded-bottom-2">
          <Blankslate>
            <Blankslate.Visual>
              <AlertIcon size="medium" className="fgColor-muted" />
            </Blankslate.Visual>
            <Blankslate.Heading as="h4">There was an issue loading this file</Blankslate.Heading>
            <Blankslate.Description>
              {' '}
              Try refreshing the page or if the problem persists{' '}
              <a className="fgColor-muted" href="https://support.github.com/">
                <u>contact support</u>
              </a>
              .
            </Blankslate.Description>
          </Blankslate>
        </div>
      )}
    </div>
  )
}

/**
 * Represents a single file diff.
 *
 * @params DiffContextProps
 * @returns Diff
 */

export const Diff = memo(DiffUnmemoized)

try{ DiffUnmemoized.displayName ||= 'DiffUnmemoized' } catch {}
try{ DiffErrorFallback.displayName ||= 'DiffErrorFallback' } catch {}
try{ Diff.displayName ||= 'Diff' } catch {}